import { useCallback, useEffect, useRef } from "react";
import styles from "../ResourcesPlanner.module.scss";
import { DRAG_THRESHOLD, DragType, previewHours, resolveDrag } from "../engine/barDrag";
import { dayToIso, isWeekendDay } from "../engine/dayMath";
import { DAY_WIDTHS } from "../engine/timelineGeometry";
import { usePlannerEngine } from "../plannerContext";

const DRAG_TYPES = { left: DragType.RESIZE_LEFT, move: DragType.MOVE, right: DragType.RESIZE_RIGHT };

/** Rows of these kinds can be dragged across to create a task. */
const CREATABLE_ROWS = new Set(["project", "scope", "taskGroup"]);

/**
 * Everything the user does with the mouse on the timeline body, handled once for all bars and rows instead of with
 * handlers on each: moving and resizing a bar, a click on a bar, the menu of a bar, dragging across a row to create
 * a task, and the hover label that shows an allocation's hours.
 *
 * While the pointer is dragging, bars, the selection and the label are moved by writing to the DOM directly, so the
 * drag costs no React renders; React only hears about it when it ends.
 *
 * @param bodyRef  the timeline body, the offset parent of everything the handlers position
 * @param selectionRef  the highlight shown while dragging across a row
 * @param pillRef  the hover label
 * @param onMenu  called with `{ top, left, resourceId, taskId }` when the menu of a bar is requested
 */
export default function useTimelineInteractions({ bodyRef, selectionRef, pillRef, onMenu }) {
  const engine = usePlannerEngine();
  const hovered = useRef(null);
  const busy = useRef(false);
  const cleanup = useRef(null);

  // Whatever is still being dragged when the screen goes away is let go of.
  useEffect(() => () => cleanup.current?.(), []);

  const dayWidth = () => DAY_WIDTHS[engine.viewStore.getState().viewType];
  const showHours = () => engine.viewStore.getState().showHours;

  const allocationOf = (bar) => engine.dataStore.getState().data.allocs[bar.dataset.r]?.byTask[bar.dataset.t] ?? null;

  const dayAt = (clientX) => {
    const rect = bodyRef.current.getBoundingClientRect();
    return engine.timeline.originDay + Math.floor((clientX - rect.left) / dayWidth());
  };

  // ── The hours label of a bar ────────────────────────────────────────────

  const hidePill = useCallback(() => {
    if (pillRef.current) pillRef.current.style.display = "none";
  }, [pillRef]);

  const showPill = useCallback(
    (bar, allocation, days) => {
      const pill = pillRef.current;
      if (!pill || !bodyRef.current || !showHours()) return;

      let hoursPerDay = allocation.hoursPerDay;
      let totalHours = allocation.totalHours;
      if (days) totalHours = previewHours(allocation, days.startDay, days.endDay).totalHours;

      pill.firstChild.textContent = hoursPerDay === 0 && totalHours === 0 ? "Assigned" : `${hoursPerDay}h • ${totalHours}hrs`;

      const barRect = bar.getBoundingClientRect();
      const bodyRect = bodyRef.current.getBoundingClientRect();
      pill.style.left = `${barRect.left - bodyRect.left - 80}px`;
      pill.style.top = `${barRect.top - bodyRect.top - 2}px`;
      pill.style.display = "block";
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pillRef, bodyRef, engine]
  );

  const onMouseOver = useCallback(
    (event) => {
      if (busy.current) return;

      const bar = event.target.closest("[data-bar]");
      if (bar === hovered.current) return;
      hovered.current = bar;

      const allocation = bar && allocationOf(bar);
      if (allocation) showPill(bar, allocation);
      else hidePill();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [engine, showPill, hidePill]
  );

  const onMouseLeave = useCallback(() => {
    hovered.current = null;
    if (!busy.current) hidePill();
  }, [hidePill]);

  // ── Moving and resizing a bar ───────────────────────────────────────────

  const beginBarDrag = useCallback(
    (event, handle) => {
      const bar = handle.closest("[data-bar]");
      const allocation = bar && allocationOf(bar);
      if (!allocation || allocation.startDay === null) return;

      event.preventDefault();
      event.stopPropagation();

      const type = DRAG_TYPES[handle.dataset.handle];
      const width = dayWidth();
      const { timeline, actions } = engine;
      const startX = event.clientX;
      const startY = event.clientY;
      let moved = false;
      let days = { startDay: allocation.startDay, endDay: allocation.endDay };

      busy.current = true;

      const onMove = (moveEvent) => {
        const dx = moveEvent.clientX - startX;
        const dy = moveEvent.clientY - startY;
        if (!moved && Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) return;

        if (!moved) {
          moved = true;
          bar.classList.add(styles.allocationBarDragging);
        }

        days = resolveDrag({ type, startDay: allocation.startDay, endDay: allocation.endDay, dx, dayWidth: width, timeline });
        bar.style.left = `${(days.startDay - timeline.originDay) * width + 2}px`;
        bar.style.width = `${(days.endDay - days.startDay + 1) * width - 2}px`;
        showPill(bar, allocation, days);
      };

      const finish = () => {
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
        cleanup.current = null;
        busy.current = false;
        bar.classList.remove(styles.allocationBarDragging);
        hidePill();
      };

      const onUp = () => {
        finish();

        // A press that did not move is a click, which opens the task.
        if (!moved) {
          actions.openEditTask(allocation.taskId);
          return;
        }

        if (days.startDay !== allocation.startDay || days.endDay !== allocation.endDay) {
          actions.updateAllocation(allocation.resourceId, allocation.taskId, dayToIso(days.startDay), dayToIso(days.endDay));
        } else {
          bar.style.left = `${(allocation.startDay - timeline.originDay) * width + 2}px`;
          bar.style.width = `${(allocation.endDay - allocation.startDay + 1) * width - 2}px`;
        }
      };

      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
      cleanup.current = finish;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [engine, showPill, hidePill]
  );

  // ── Dragging across a row to create a task ──────────────────────────────

  const beginSelection = useCallback(
    (event, rowElement) => {
      const startDay = dayAt(event.clientX);
      if (isWeekendDay(startDay)) return;

      event.preventDefault();

      const { timeline, actions, dataStore } = engine;
      const width = dayWidth();
      const overlay = selectionRef.current;
      const bodyRect = bodyRef.current.getBoundingClientRect();
      const rowRect = rowElement.getBoundingClientRect();
      let range = { startDay, endDay: startDay };

      const draw = () => {
        overlay.style.left = `${(range.startDay - timeline.originDay) * width}px`;
        overlay.style.width = `${(range.endDay - range.startDay + 1) * width}px`;
        overlay.style.top = `${rowRect.top - bodyRect.top + 4}px`;
        overlay.style.height = `${rowRect.height - 8}px`;
        overlay.style.display = "block";
      };

      busy.current = true;
      draw();

      const onMove = (moveEvent) => {
        const day = dayAt(moveEvent.clientX);
        if (isWeekendDay(day)) return;

        range = { startDay: Math.min(startDay, day), endDay: Math.max(startDay, day) };
        draw();
      };

      const finish = () => {
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
        cleanup.current = null;
        busy.current = false;
        overlay.style.display = "none";
      };

      const onUp = (upEvent) => {
        finish();

        const insideBody = bodyRef.current?.contains(upEvent.target);
        if (!insideBody) return;

        const { data } = dataStore.getState();
        const { resource: resourceId, project: projectId, scope: scopeId, group: groupId } = rowElement.dataset;
        const resource = data.resources[resourceId];
        if (!resource) return;

        const named = (node) => (node ? { id: node.id, name: node.name } : null);
        actions.openCreateTask(
          resource,
          dayToIso(range.startDay),
          dayToIso(range.endDay),
          named(data.projects[projectId]),
          named(data.scopes[scopeId]),
          named(data.groups[groupId])
        );
      };

      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
      cleanup.current = finish;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [engine, bodyRef, selectionRef]
  );

  const onMouseDown = useCallback(
    (event) => {
      if (event.button !== 0) return;

      const handle = event.target.closest("[data-handle]");
      if (handle) {
        beginBarDrag(event, handle);
        return;
      }

      const row = event.target.closest("[data-row-key]");
      if (row && CREATABLE_ROWS.has(row.dataset.rowType)) beginSelection(event, row);
    },
    [beginBarDrag, beginSelection]
  );

  // ── The menu of a bar ───────────────────────────────────────────────────

  const onContextMenu = useCallback(
    (event) => {
      const bar = event.target.closest("[data-bar]");
      if (!bar) return;

      event.preventDefault();
      event.stopPropagation();

      const menuHeight = 80;
      const top = event.clientY + menuHeight > window.innerHeight ? event.clientY - menuHeight : event.clientY;
      onMenu({ top, left: event.clientX, resourceId: bar.dataset.r, taskId: bar.dataset.t });
    },
    [onMenu]
  );

  return { onMouseDown, onMouseOver, onMouseLeave, onContextMenu };
}
